import { CommonModule } from '@angular/common';
import { AfterViewChecked, Component, ElementRef, ViewChild, computed, effect, signal } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

import { DocumentsEnhancedData } from '../../../../models/workspace-overview.model';
import { WorkspaceOverviewStateService } from '../../../../services/workspace-overview-state.service';
import { WorkspaceOverviewService } from '../../../../services/workspace-overview.service';

type CountBucket = 'lt5' | 'btw5_10' | 'btw11_20' | 'gt20';

const BUCKET_STYLES: Record<CountBucket, { bg: string; text: string }> = {
    lt5: { bg: '#F3F5FF', text: '#1C1C1C' },
    btw5_10: { bg: '#E7EBFF', text: '#1C1C1C' },
    btw11_20: { bg: '#BBC6FF', text: '#1C1C1C' },
    gt20: { bg: '#8A9EFF', text: '#1C1C1C' },
};

/**
 * Documents Enhanced Widget
 *
 * Feature × date chip table. Has its own independent date-range picker so
 * it is not coupled to the global Period toggle. Reads `viewMode` from
 * `WorkspaceOverviewStateService`; re-fetches when mode changes.
 *
 * Tooltip: "Displays the total number of documents on which AI features
 * have been applied".
 */
@Component({
    selector: 'app-documents-enhanced-widget',
    standalone: true,
    imports: [CommonModule, TooltipModule, SkeletonModule],
    templateUrl: './documents-enhanced-widget.component.html',
    styleUrl: './documents-enhanced-widget.component.scss',
})
export class DocumentsEnhancedWidgetComponent implements AfterViewChecked {
    @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLDivElement>;

    readonly data = signal<DocumentsEnhancedData | null>(null);
    readonly loading = signal(false);
    readonly scrollCursor = signal<string>('default');

    readonly legend: { label: string; bucket: CountBucket }[] = [
        { label: '<5', bucket: 'lt5' },
        { label: '6-10', bucket: 'btw5_10' },
        { label: '11-20', bucket: 'btw11_20' },
        { label: '>20', bucket: 'gt20' },
    ];
    readonly bucketStyles = BUCKET_STYLES;

    readonly columnLabels = computed(() =>
        (this.data()?.dateColumns ?? []).map(d => {
            const dt = new Date(d);
            return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
        })
    );

    constructor(
        private readonly service: WorkspaceOverviewService,
        private readonly state: WorkspaceOverviewStateService,
    ) {
        effect(() => {
            const mode = this.state.viewMode();
            const period = this.state.period();
            const range = this.state.effectiveRange();
            this.fetch({ mode, period, range });
        });
    }

    ngAfterViewChecked(): void {
        const el = this.scrollContainer?.nativeElement;
        if (!el) return;
        const canV = el.scrollHeight > el.clientHeight;
        const canH = el.scrollWidth > el.clientWidth;
        const cursor = canV && canH ? 'all-scroll' : canV ? 'ns-resize' : canH ? 'ew-resize' : 'default';
        if (this.scrollCursor() !== cursor) this.scrollCursor.set(cursor);
    }

    formatDate(dateStr: string): string {
        const dt = new Date(dateStr);
        return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
    }

    bucketFor(count: number): CountBucket {
        if (count <= 5) return 'lt5';
        if (count <= 10) return 'btw5_10';
        if (count <= 20) return 'btw11_20';
        return 'gt20';
    }

    private fetch(params: Parameters<WorkspaceOverviewService['getDocumentsEnhanced']>[0]): void {
        this.loading.set(true);
        this.service.getDocumentsEnhanced(params).subscribe({
            next: data => {
                const sorted: typeof data = {
                    ...data,
                    dateColumns: [...data.dateColumns].reverse(),
                    features: data.features.map(f => ({
                        ...f,
                        daily: [...f.daily].reverse(),
                    })),
                };
                this.data.set(sorted);
                this.loading.set(false);
            },
            error: () => this.loading.set(false),
        });
    }
}
